import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Radio,
  Typography,
} from '@mui/material';
import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';

const getRequestId = (row, fallback) => row?.requestId || row?.request_id || row?.id || fallback;
const getGroupLabel = (row) => row?.request?.xptPgnDesc || row?.request?.npaProductSum || '-';

const DrugMatching = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { execute } = useAPICall();
  const [sessionData, setSessionData] = useState(null);
  const [requestRows, setRequestRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const key = searchParams.get('key');
    if (!key) {
      setError('Missing duplicate selection context.');
      return;
    }

    const stored = sessionStorage.getItem(key);
    if (!stored) {
      setError('Duplicate selection context is not available.');
      return;
    }

    try {
      const parsed = JSON.parse(stored);
      setSessionData(parsed);
      setRequestRows(Array.isArray(parsed?.selected) ? parsed.selected : []);
    } catch {
      setError('Unable to read duplicate selection context.');
    }
  }, [searchParams]);

  const groupHeader = useMemo(() => (
    requestRows[0]?.request?.source === 'NPA' ? 'Product Sum' : 'Group'
  ), [requestRows]);

  const toggleMatch = (requestId, pxId) => {
    setSessionData((prev) => {
      if (!prev?.dcr?.[0]?.batchRequests) return prev;
      const next = structuredClone(prev);
      next.dcr[0].batchRequests = next.dcr[0].batchRequests.map((row, index) => {
        const rowRequestId = getRequestId(row, index);
        if (rowRequestId !== requestId) return row;
        return {
          ...row,
          ndcReferences: (row.ndcReferences || []).map((reference) => ({
            ...reference,
            isMatched: reference.pxId === pxId,
          })),
        };
      });
      return next;
    });

    setRequestRows((prev) => prev.map((row, index) => {
      const rowRequestId = getRequestId(row, index);
      if (rowRequestId !== requestId) return row;
      return {
        ...row,
        ndcReferences: (row.ndcReferences || []).map((reference) => ({
          ...reference,
          isMatched: reference.pxId === pxId,
        })),
      };
    }));
  };

  const saveChanges = async () => {
    const hasMatch = requestRows.every((row) => Array.isArray(row.ndcReferences)
      && row.ndcReferences.some((reference) => reference.isMatched));

    if (!hasMatch) {
      setError('Please select an NDC to be matched.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      await execute(API_CONFIG.drugDcr.updateDcr, sessionData.dcr, {
        successMessage: 'Process completed successfully.',
        showSuccessToast: true,
        errorMessage: 'Unable to save duplicate resolution.',
      });
      navigate(`/approve-ndc?batch-id=${sessionData?.dcr?.[0]?.batchId}&status=un-matched`);
    } catch {
    } finally {
      setSaving(false);
    }
  };

  if (error && !sessionData) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Drug Matching</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Resolve duplicate NDCs from the selected drug inbox batch.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!sessionData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Typography variant="body2"><strong>Batch ID:</strong> {sessionData?.dcr?.[0]?.batchId || '-'}</Typography>
            <Typography variant="body2"><strong>Selected Duplicates:</strong> {requestRows.length}</Typography>
          </Paper>

          {requestRows.map((row, index) => {
            const requestId = getRequestId(row, index);
            return (
              <Paper key={requestId} sx={{ p: 2.5, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  NDC {row?.request?.ndc || '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Source: {row?.request?.source || '-'} | {groupHeader}: {getGroupLabel(row)}
                </Typography>

                <GlobalMaterialTable
                  columns={[
                    {
                      header: 'Match',
                      id: 'match',
                      enableSorting: false,
                      Cell: ({ row: rowItem }) => (
                        <Radio
                          checked={Boolean(rowItem.original.isMatched)}
                          onChange={() => toggleMatch(requestId, rowItem.original.pxId)}
                        />
                      ),
                    },
                    {
                      header: 'NDC',
                      id: 'ndc',
                      accessorFn: () => row?.request?.ndc || '-',
                    },
                    {
                      header: 'Drug ID',
                      id: 'drugId',
                      accessorFn: (reference) => reference.pxId || '-',
                    },
                    {
                      header: 'Description',
                      id: 'description',
                      accessorFn: (reference) => reference.pxLongDescription || '-',
                    },
                    {
                      header: 'Start Date',
                      id: 'startDate',
                      accessorFn: (reference) => reference.pxStartDate || '-',
                    },
                    {
                      header: 'End Date',
                      id: 'endDate',
                      accessorFn: (reference) => reference.pxEndDate || '-',
                    },
                    {
                      header: 'Status',
                      id: 'status',
                      accessorFn: (reference) => reference.pxStatus || '-',
                    },
                    {
                      header: 'RXCUI',
                      id: 'rxcui',
                      accessorFn: (reference) => reference.pxRxcui || '-',
                    },
                  ]}
                  data={Array.isArray(row.ndcReferences) ? row.ndcReferences : []}
                  emptyMessage="No matching references found"
                  enablePagination={false}
                  enableBottomToolbar={false}
                  muiTablePaperProps={{ elevation: 0, sx: { borderRadius: 2 } }}
                />
              </Paper>
            );
          })}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button className="action-btn cancel-btn" variant="outlined" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
            <Button className="action-btn process-btn" variant="contained" onClick={saveChanges} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default DrugMatching;
